import { useState } from "react";
import axios from "axios";
import { Loader2, Upload, CheckCircle2, ArrowRight, X, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";
import { TREATMENTS } from "../data/treatments";
import { LOCATIONS } from "../data/locations";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024; // 5MB

export default function ConsultationForm({ defaultTreatment = "", defaultCity = "" }) {
  const { t, lang } = useI18n();
  const f = t.form;

  const [form, setForm] = useState({
    fullName: "",
    country: "",
    phone: "",
    email: "",
    treatment: defaultTreatment,
    city: defaultCity,
    travelDate: "",
    message: "",
    consent: false,
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = f.errorRequired;
    if (!form.email.trim()) e.email = f.errorRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = f.errorEmail;
    if (!form.phone.trim()) e.phone = f.errorRequired;
    if (!form.consent) e.consent = f.errorConsent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onFile = (ev) => {
    const file = ev.target.files?.[0];
    setFileError("");
    if (!file) return setFile(null);
    if (file.size > MAX_BYTES) {
      setFileError(f.uploadHint);
      setFile(null);
      return;
    }
    setFile(file);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      let attachment = null;
      if (file) {
        attachment = {
          filename: file.name,
          content_type: file.type,
          data_base64: await fileToBase64(file),
        };
      }
      await axios.post(`${API}/consultations`, {
        ...form,
        language: lang,
        source_url: typeof window !== "undefined" ? window.location.href : "",
        attachment,
      });
      setSuccess(true);
    } catch (err) {
      setErrors((e) => ({ ...e, _global: f.errorGeneric }));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="consultation-success"
        className="rounded-3xl bg-white p-8 sm:p-10 shadow-soft border border-turquoise/20 text-center"
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-turquoise/10">
          <CheckCircle2 className="h-8 w-8 text-turquoise" />
        </div>
        <h3 className="mt-6 text-2xl text-navy">{f.successTitle}</h3>
        <p className="mt-3 text-navy/70 leading-relaxed">{f.successBody}</p>
      </div>
    );
  }

  const fieldCls = "h-13 min-h-[3.25rem] rounded-xl border-navy/15 bg-white text-base focus-visible:ring-dental";

  return (
    <form
      onSubmit={onSubmit}
      data-testid="consultation-form"
      className="rounded-3xl bg-white p-6 sm:p-8 shadow-soft border border-navy/10"
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="fullName" className="text-navy font-semibold">{f.fullName} *</Label>
          <Input id="fullName" data-testid="form-fullname" value={form.fullName}
            onChange={(e) => set("fullName", e.target.value)} className={`mt-2 ${fieldCls}`} />
          {errors.fullName && <p className="mt-1 text-sm text-destructive" data-testid="error-fullname">{errors.fullName}</p>}
        </div>

        <div>
          <Label htmlFor="country" className="text-navy font-semibold">{f.country}</Label>
          <Input id="country" data-testid="form-country" value={form.country}
            onChange={(e) => set("country", e.target.value)} className={`mt-2 ${fieldCls}`} />
        </div>

        <div>
          <Label htmlFor="phone" className="text-navy font-semibold">{f.phone} *</Label>
          <Input id="phone" data-testid="form-phone" value={form.phone} inputMode="tel"
            onChange={(e) => set("phone", e.target.value)} className={`mt-2 ${fieldCls}`} />
          {errors.phone && <p className="mt-1 text-sm text-destructive" data-testid="error-phone">{errors.phone}</p>}
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="email" className="text-navy font-semibold">{f.email} *</Label>
          <Input id="email" type="email" data-testid="form-email" value={form.email}
            onChange={(e) => set("email", e.target.value)} className={`mt-2 ${fieldCls}`} />
          {errors.email && <p className="mt-1 text-sm text-destructive" data-testid="error-email">{errors.email}</p>}
        </div>

        <div>
          <Label className="text-navy font-semibold">{f.treatment}</Label>
          <Select value={form.treatment} onValueChange={(v) => set("treatment", v)}>
            <SelectTrigger data-testid="form-treatment" className={`mt-2 ${fieldCls}`}>
              <SelectValue placeholder={f.selectTreatment} />
            </SelectTrigger>
            <SelectContent>
              {TREATMENTS.map((tr) => (
                <SelectItem key={tr.key} value={tr.key}>{tr.name[lang] || tr.name.en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-navy font-semibold">{f.city}</Label>
          <Select value={form.city} onValueChange={(v) => set("city", v)}>
            <SelectTrigger data-testid="form-city" className={`mt-2 ${fieldCls}`}>
              <SelectValue placeholder={f.selectCity} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{f.noPreference}</SelectItem>
              {LOCATIONS.map((lo) => (
                <SelectItem key={lo.key} value={lo.key}>{lo.name[lang] || lo.name.en}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="travelDate" className="text-navy font-semibold">{f.travelDate}</Label>
          <Input id="travelDate" data-testid="form-traveldate" value={form.travelDate}
            placeholder="e.g. March 2026" onChange={(e) => set("travelDate", e.target.value)} className={`mt-2 ${fieldCls}`} />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="message" className="text-navy font-semibold">{f.message}</Label>
          <Textarea id="message" data-testid="form-message" rows={4} value={form.message}
            onChange={(e) => set("message", e.target.value)} className="mt-2 rounded-xl border-navy/15 text-base focus-visible:ring-dental" />
        </div>

        <div className="sm:col-span-2">
          <Label className="text-navy font-semibold">{f.upload}</Label>
          <label
            htmlFor="attachment"
            data-testid="form-upload-label"
            className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-navy/25 bg-mist/50 px-4 py-4 hover:border-dental transition-colors"
          >
            <Upload className="h-5 w-5 text-dental shrink-0" />
            <span className="text-sm text-navy/70 truncate">
              {file ? file.name : f.uploadHint}
            </span>
            {file && (
              <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }}
                className="ml-auto text-navy/40 hover:text-destructive" aria-label="Remove file">
                <X className="h-4 w-4" />
              </button>
            )}
            <input id="attachment" type="file" data-testid="form-upload-input"
              accept="image/*,application/pdf" className="sr-only" onChange={onFile} />
          </label>
          {fileError && <p className="mt-1 text-sm text-destructive">{fileError}</p>}
        </div>

        <div className="sm:col-span-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={form.consent}
              onCheckedChange={(v) => set("consent", !!v)}
              data-testid="form-consent"
              className="mt-0.5 data-[state=checked]:bg-dental data-[state=checked]:border-dental"
            />
            <span className="text-sm text-navy/70 leading-relaxed">{f.consent}</span>
          </label>
          {errors.consent && <p className="mt-1 text-sm text-destructive" data-testid="error-consent">{errors.consent}</p>}
        </div>
      </div>

      {errors._global && (
        <p className="mt-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="form-global-error">
          {errors._global}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="form-submit"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-dental px-6 py-4 text-base font-semibold text-white hover:bg-dental-dark transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
        {submitting ? f.submitting : f.submit}
      </button>

      <p className="mt-4 flex items-center justify-center gap-2 text-xs text-navy/50">
        <ShieldCheck className="h-4 w-4 text-turquoise" />
        {t.hero.microC} · {t.hero.microB}
      </p>
    </form>
  );
}
